(() => {
  if (customElements.get('wc-shop-carousel')) return;

  class WcShopCarousel extends HTMLElement {
    constructor() {
      super();
      this.swiperInstance = null;
      this.boundResize = this.debounce(this.handleResize.bind(this), 150);
      this.copyHandler = this.handleCopyClick.bind(this);
    }

    connectedCallback() {
      this.sectionElement = this.closest('[data-wc-shop-carousel-section]');
      this.swiperElement = this.querySelector('.wc-shop__swiper');
      this.mobileBreakpoint = parseInt(this.dataset.mobileBreakpoint || '767', 10);
      this.enableLoop = this.dataset.loop === 'true';

      this.markAltHeadingSlides();
      this.bindCopyButtons();

      this.waitForSwiper(() => {
        this.handleResize();
        window.addEventListener('resize', this.boundResize, { passive: true });
      });
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.boundResize);
      this.unbindCopyButtons();
      this.destroySwiper();
      this.classList.remove('wc-shop__component--stacked');
    }

    debounce(fn, wait) {
      let timer = null;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), wait);
      };
    }

    waitForSwiper(callback, retry = 0) {
      if (typeof window.Swiper === 'function') {
        callback();
        return;
      }
      if (retry > 20) return;
      setTimeout(() => this.waitForSwiper(callback, retry + 1), 120);
    }

    isMobile() {
      return window.innerWidth <= this.mobileBreakpoint;
    }

    resolveGap() {
      const prop = this.isMobile() ? '--slide-gap-m' : '--slide-gap';
      const raw = this.sectionElement
        ? window.getComputedStyle(this.sectionElement).getPropertyValue(prop)
        : '';
      const gap = parseFloat(raw);
      return Number.isFinite(gap) ? gap : this.isMobile() ? 20 : 40;
    }

    resolveSlidesPerView() {
      const raw = this.isMobile()
        ? this.dataset.slidesPerViewM
        : this.dataset.slidesPerViewD;

      if (!raw || raw === 'auto') return 'auto';

      const value = parseFloat(raw);
      return Number.isFinite(value) ? value : 'auto';
    }

    markAltHeadingSlides() {
      this.querySelectorAll('.wc-shop__slide').forEach((slide) => {
        const altHeading = slide.querySelector('.wc-shop__alt-heading');
        slide.classList.toggle(
          'wc-shop__slide--has-alt-heading',
          Boolean(altHeading && altHeading.textContent.trim())
        );
      });
    }

    bindCopyButtons() {
      this.querySelectorAll('[data-wc-copy-code]').forEach((button) => {
        button.addEventListener('click', this.copyHandler);
      });
    }

    unbindCopyButtons() {
      this.querySelectorAll('[data-wc-copy-code]').forEach((button) => {
        button.removeEventListener('click', this.copyHandler);
      });
    }

    handleCopyClick(event) {
      const button = event.currentTarget;
      const code = button.dataset.wcCopyCode;
      if (!code) return;

      const copiedLabel = button.dataset.copiedLabel || 'Copied!';
      const defaultLabel = button.textContent.trim();

      const onSuccess = () => {
        button.classList.add('is-copied');
        button.textContent = copiedLabel;
        window.setTimeout(() => {
          button.classList.remove('is-copied');
          button.textContent = defaultLabel;
        }, 1800);
      };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(onSuccess).catch(() => {});
        return;
      }

      const textarea = document.createElement('textarea');
      textarea.value = code;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        onSuccess();
      } catch (error) {
        /* noop */
      }
      document.body.removeChild(textarea);
    }

    handleResize() {
      if (!this.swiperElement) return;

      if (this.isMobile()) {
        this.classList.add('wc-shop__component--stacked');
        this.destroySwiper();
        return;
      }

      this.classList.remove('wc-shop__component--stacked');

      const slidesPerView = this.resolveSlidesPerView();
      const spaceBetween = this.resolveGap();

      if (this.swiperInstance) {
        this.swiperInstance.params.slidesPerView = slidesPerView;
        this.swiperInstance.params.spaceBetween = spaceBetween;
        this.swiperInstance.update();
        return;
      }

      this.initSwiper(slidesPerView, spaceBetween);
    }

    initSwiper(slidesPerView, spaceBetween) {
      if (this.swiperInstance || typeof window.Swiper !== 'function') return;

      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.swiperInstance = new window.Swiper(this.swiperElement, {
        slidesPerView,
        spaceBetween,
        watchOverflow: true,
        speed: prefersReducedMotion ? 0 : 600,
        loop: this.enableLoop,
        grabCursor: true,
        slideToClickedSlide: false
      });
    }

    destroySwiper() {
      if (!this.swiperInstance) return;
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
  }

  customElements.define('wc-shop-carousel', WcShopCarousel);
})();
